import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import store, { Package } from '../Store';

const PackagesPage: React.FC = observer(() => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [editPackage, setEditPackage] = useState({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    type: 'subscription' as 'subscription' | 'advertisement' | 'premium',
    category: '' as '' | 'subscription' | 'advertisement_with_image' | 'advertisement_without_image' | 'premium',
    price: 0,
    currency: 'EUR',
    duration: 1,
    duration_days: 30,
    is_active: true,
    is_default: false,
    is_featured: false,
    icon_path: ''
  });
  const [newPackage, setNewPackage] = useState({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    type: 'subscription' as 'subscription' | 'advertisement' | 'premium',
    category: '' as '' | 'subscription' | 'advertisement_with_image' | 'advertisement_without_image' | 'premium',
    price: 0,
    currency: 'EUR',
    duration: 1,
    duration_days: 30,
    is_active: true,
    is_default: false,
    is_featured: false,
    icon_path: ''
  });

  useEffect(() => {
    store.fetchPackages();
  }, []);

  const filteredPackages = store.packages.filter(pkg => {
    return pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           pkg.description.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleCreatePackage = async () => {
    try {
      await store.createPackage(newPackage);
      setShowCreateModal(false);
      setNewPackage({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        type: 'subscription',
        category: '',
        price: 0,
        currency: 'EUR',
        duration: 1,
        duration_days: 30,
        is_active: true,
        is_default: false,
        is_featured: false,
        icon_path: ''
      });
      store.fetchPackages();
    } catch (error) {
      console.error('Error creating package:', error);
    }
  };

  const handleEditPackage = async () => {
    if (!selectedPackage) return;
    try {
      await store.updatePackage(selectedPackage.id, editPackage);
      setShowEditModal(false);
      setSelectedPackage(null);
      store.fetchPackages();
    } catch (error) {
      console.error('Error updating package:', error);
    }
  };


  const handleDeletePackage = async (packageId: string) => {
    if (globalThis.confirm('هل أنت متأكد من حذف هذه الباقة؟')) {
      try {
        await store.deletePackage(packageId);
        store.fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
      }
    }
  };

  const handleTogglePackageStatus = async (packageId: string, isActive: boolean) => {
    try {
      await store.togglePackageStatus(packageId, isActive);
      store.fetchPackages();
    } catch (error) {
      console.error('Error toggling package status:', error);
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">إدارة الباقات</h1>
          <p className="text-gray-600 dark:text-gray-400">عرض وإدارة جميع الباقات في النظام</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          إضافة باقة جديدة
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            البحث في الباقات
          </label>
          <input
            type="text"
            placeholder="البحث في الباقات..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPackages.map((pkg) => (
          <div key={pkg.id} className={`bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden border-2 transition-all duration-300 hover:shadow-xl ${
            pkg.is_featured ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20' : 'border-gray-200 dark:border-gray-700'
          }`}>
            <div className="p-6">
              {/* Header with Icon and Type */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3 space-x-reverse">
                  {pkg.icon_url ? (
                    <img src={pkg.icon_url} alt={pkg.name} className="w-10 h-10 rounded-lg" />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {pkg.name_ar || pkg.name}
                    </h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      pkg.type === 'subscription' 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                    }`}>
                      {pkg.type === 'subscription' ? 'اشتراك' : 'إعلان'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    pkg.is_active 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {pkg.is_active ? 'نشط' : 'غير نشط'}
                  </span>
                  {pkg.is_featured && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      مميز
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                {pkg.description_ar || pkg.description}
              </p>
              
              {/* Price and Duration */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {pkg.price === 0 ? 'مجاني' : `€${pkg.price}`}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2 text-sm">
                      {pkg.currency}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {(pkg.type === 'subscription' || pkg.type === 'premium')
                        ? `لمدة ${pkg.duration} ${pkg.duration === 1 ? 'شهر' : 'أشهر'}`
                        : `لمدة ${pkg.duration_days} ${pkg.duration_days === 1 ? 'يوم' : 'أيام'}`
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex space-x-2 space-x-reverse">
                <button
                  onClick={() => {
                    setSelectedPackage(pkg);
                    setEditPackage({
                      name: pkg.name,
                      name_ar: pkg.name_ar || '',
                      description: pkg.description,
                      description_ar: pkg.description_ar || '',
                      type: pkg.type,
                      category: pkg.category || '',
                      price: pkg.price,
                      currency: pkg.currency,
                      duration: pkg.duration,
                      duration_days: pkg.duration_days || 30,
                      is_active: pkg.is_active,
                      is_default: pkg.is_default,
                      is_featured: pkg.is_featured,
                      icon_path: pkg.icon_path || ''
                    });
                    setShowEditModal(true);
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  تعديل
                </button>
                <button
                  onClick={() => handleTogglePackageStatus(pkg.id, pkg.is_active)}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 transition-colors duration-200 ${
                    pkg.is_active
                      ? 'text-red-600 bg-red-100 hover:bg-red-200 focus:ring-red-500'
                      : 'text-green-600 bg-green-100 hover:bg-green-200 focus:ring-green-500'
                  }`}
                >
                  {pkg.is_active ? 'إلغاء التفعيل' : 'تفعيل'}
                </button>
                <button
                  onClick={() => handleDeletePackage(pkg.id)}
                  className="px-3 py-2 text-sm font-medium text-red-600 bg-red-100 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPackages.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">لا توجد باقات مطابقة للبحث</p>
        </div>
      )}

      {/* Create Package Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">إضافة باقة جديدة</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      اسم الباقة (إنجليزي)
                    </label>
                    <input
                      type="text"
                      value={newPackage.name}
                      onChange={(e) => setNewPackage({...newPackage, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      اسم الباقة (عربي)
                    </label>
                    <input
                      type="text"
                      value={newPackage.name_ar}
                      onChange={(e) => setNewPackage({...newPackage, name_ar: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      نوع الباقة
                    </label>
                    <select
                      value={newPackage.type}
                      onChange={(e) => setNewPackage({...newPackage, type: e.target.value as 'subscription' | 'advertisement' | 'premium'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="subscription">اشتراك</option>
                      <option value="advertisement">إعلان</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      فئة الباقة
                    </label>
                    <select
                      value={newPackage.category}
                      onChange={(e) => setNewPackage({...newPackage, category: e.target.value as '' | 'subscription' | 'advertisement_with_image' | 'advertisement_without_image' | 'premium'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">اختر الفئة</option>
                      <option value="subscription">اشتراك</option>
                      <option value="advertisement_with_image">إعلان مع صورة</option>
                      <option value="advertisement_without_image">إعلان بدون صورة أو مع تصميم</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      الوصف (إنجليزي)
                    </label>
                    <textarea
                      value={newPackage.description}
                      onChange={(e) => setNewPackage({...newPackage, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      الوصف (عربي)
                    </label>
                    <textarea
                      value={newPackage.description_ar}
                      onChange={(e) => setNewPackage({...newPackage, description_ar: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      السعر (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPackage.price}
                      onChange={(e) => {
                        const val = Number.parseFloat(e.target.value) || 0;
                        setNewPackage({...newPackage, price: Math.max(0, val)});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {newPackage.price === 0 && (
                      <p className="text-green-600 text-xs mt-1">✓ الباقة مجانية</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      العملة
                    </label>
                    <select
                      value={newPackage.currency}
                      onChange={(e) => setNewPackage({...newPackage, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                
                {(newPackage.type === 'subscription' || newPackage.type === 'premium') ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المدة (بالأشهر)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newPackage.duration}
                      onChange={(e) => {
                        const val = Number.parseInt(e.target.value, 10) || 1;
                        setNewPackage({...newPackage, duration: Math.max(1, val)});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المدة (بالأيام)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newPackage.duration_days}
                      onChange={(e) => {
                        const val = Number.parseInt(e.target.value, 10) || 1;
                        setNewPackage({...newPackage, duration_days: Math.max(1, val)});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newPackage.is_active}
                      onChange={(e) => setNewPackage({...newPackage, is_active: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">
                      نشط
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newPackage.is_default}
                      onChange={(e) => setNewPackage({...newPackage, is_default: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">
                      افتراضي
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newPackage.is_featured}
                      onChange={(e) => setNewPackage({...newPackage, is_featured: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">
                      مميز
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleCreatePackage}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  إضافة
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Package Modal */}
      {showEditModal && selectedPackage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">تعديل الباقة</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      اسم الباقة (إنجليزي)
                    </label>
                    <input
                      type="text"
                      value={editPackage.name}
                      onChange={(e) => setEditPackage({...editPackage, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      اسم الباقة (عربي)
                    </label>
                    <input
                      type="text"
                      value={editPackage.name_ar}
                      onChange={(e) => setEditPackage({...editPackage, name_ar: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      نوع الباقة
                    </label>
                    <select
                      value={editPackage.type}
                      onChange={(e) => setEditPackage({...editPackage, type: e.target.value as 'subscription' | 'advertisement' | 'premium'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="subscription">اشتراك</option>
                      <option value="advertisement">إعلان</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      فئة الباقة
                    </label>
                    <select
                      value={editPackage.category}
                      onChange={(e) => setEditPackage({...editPackage, category: e.target.value as '' | 'subscription' | 'advertisement_with_image' | 'advertisement_without_image' | 'premium'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">اختر الفئة</option>
                      <option value="subscription">اشتراك</option>
                      <option value="advertisement_with_image">إعلان مع صورة</option>
                      <option value="advertisement_without_image">إعلان بدون صورة أو مع تصميم</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      الوصف (إنجليزي)
                    </label>
                    <textarea
                      value={editPackage.description}
                      onChange={(e) => setEditPackage({...editPackage, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      الوصف (عربي)
                    </label>
                    <textarea
                      value={editPackage.description_ar}
                      onChange={(e) => setEditPackage({...editPackage, description_ar: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      السعر (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editPackage.price}
                      onChange={(e) => {
                        const val = Number.parseFloat(e.target.value) || 0;
                        setEditPackage({...editPackage, price: Math.max(0, val)});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {editPackage.price === 0 && (
                      <p className="text-green-600 text-xs mt-1">✓ الباقة مجانية</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      العملة
                    </label>
                    <select
                      value={editPackage.currency}
                      onChange={(e) => setEditPackage({...editPackage, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                
                {(editPackage.type === 'subscription' || editPackage.type === 'premium') ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المدة (بالأشهر)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={editPackage.duration}
                      onChange={(e) => {
                        const val = Number.parseInt(e.target.value, 10) || 1;
                        setEditPackage({...editPackage, duration: Math.max(1, val)});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المدة (بالأيام)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={editPackage.duration_days}
                      onChange={(e) => {
                        const val = Number.parseInt(e.target.value, 10) || 1;
                        setEditPackage({...editPackage, duration_days: Math.max(1, val)});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editPackage.is_active}
                      onChange={(e) => setEditPackage({...editPackage, is_active: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">
                      نشط
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editPackage.is_default}
                      onChange={(e) => setEditPackage({...editPackage, is_default: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">
                      افتراضي
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editPackage.is_featured}
                      onChange={(e) => setEditPackage({...editPackage, is_featured: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">
                      مميز
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleEditPackage}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  حفظ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PackagesPage;
